import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ProgramSource } from "@/services/adminProgramNews";

interface SourceDisableActionProps {
  source: ProgramSource;
  /** Resolves when saved; rejects on failure (the parent toasts). */
  onDisable: (teamId: string, reason: string) => Promise<unknown>;
  onEnable: (teamId: string) => Promise<unknown>;
}

/** Disable (with a required reason) or re-enable a source. Rendered only when writable. */
const SourceDisableAction = ({ source, onDisable, onEnable }: SourceDisableActionProps) => {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);

  const canConfirm = !pending && reason.trim().length > 0;

  function close() {
    setEditing(false);
    setReason("");
  }

  async function handleDisable() {
    const text = reason.trim();
    if (!text) return;
    setPending(true);
    try {
      await onDisable(source.teamId, text);
      close();
    } catch {
      // Parent surfaces the error; keep the form open for a retry.
    } finally {
      setPending(false);
    }
  }

  async function handleEnable() {
    setPending(true);
    try {
      await onEnable(source.teamId);
    } catch {
      // Parent surfaces the error.
    } finally {
      setPending(false);
    }
  }

  if (source.status === "disabled") {
    return (
      <Button size="sm" variant="outline" onClick={handleEnable} disabled={pending}>
        {pending ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            Enabling…
          </>
        ) : (
          "Enable"
        )}
      </Button>
    );
  }

  if (!editing) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
        Disable
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2 min-w-[16rem]">
      <Input
        value={reason}
        onChange={e => setReason(e.target.value)}
        disabled={pending}
        required
        placeholder="Why this source will never have golf news"
        aria-label="Disable reason"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" variant="destructive" onClick={handleDisable} disabled={!canConfirm}>
          {pending ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Disabling…
            </>
          ) : (
            "Confirm disable"
          )}
        </Button>
        <Button size="sm" variant="ghost" onClick={close} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default SourceDisableAction;
